// src/hooks/useStripePayoutsGuard.js
// Centralized helper to gate paid publish/actions on Stripe Connect.
// Reads canonical payouts flag from studio.stripe_account_id_resolved (via useStudio),
// with fallbacks to Auth profile/studioProfile when available.

import { useCallback } from "react";
import { useStudio } from "../contexts/StudioContext";
import { useAuth } from "../contexts/AuthContext";
import { useActiveStudioId } from "./useActiveStudioId";
import { apiUrl } from "../utils/apiBase";
import { authedFetch } from "@/lib/authedFetch";

export function useStripePayoutsGuard() {
  const { studio } = useStudio() || {};
  const { profile, studioProfile, user, session } = useAuth() || {};
  const { studioId: activeStudioId } = useActiveStudioId?.() || {};

  // Treat presence of any account ID as connected; payouts_enabled is not required to hide banners.
  const stripeAccountId =
    studio?.stripe_account_id_resolved ||
    studio?.stripe_account_id ||
    studioProfile?.stripe_account_id_resolved ||
    studioProfile?.connect_account_id ||
    profile?.stripe_account_id_resolved ||
    profile?.stripe_account_id ||
    profile?.studio_stripe_account_id_resolved ||
    null;

  const ensureStripeConnected = useCallback(
    async ({ studioId } = {}) => {
    if (stripeAccountId) return { ok: true, connected: true, stripeAccountId };

    // Surface a consistent toast/banner message
    try {
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", {
          detail: {
            type: "warning",
            message:
              "Connect Stripe to receive payouts for paid listings, workshops, and studio spaces.",
          },
        })
      );
    } catch {}

    // Attempt to create onboarding link via backend
    try {
      const effectiveStudioId =
        studioId ||
        activeStudioId ||
        studio?.studioId ||
        studio?.id ||
        null;

      if (!effectiveStudioId) {
        console.warn("[StripePayoutsGuard] ensureStripeConnected called without studioId");
        try {
          window.dispatchEvent?.(
            new CustomEvent("mudo:toast", {
              detail: {
                type: "error",
                message: "Select a studio before connecting Stripe.",
              },
            })
          );
        } catch {}
        return { ok: false, connected: false, error: "studioId is required" };
      }

      const payload = { studioId: effectiveStudioId };
      const connectUrl = apiUrl("/stripe/connect-onboard");
      const legacyUrl = apiUrl("/stripe/create-onboarding-link");

      // Prefer the new endpoint; keep compatibility with the old route.
      let res = await authedFetch(connectUrl, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // Fallback if the new route isn't deployed yet
      if (res.status === 404) {
        res = await authedFetch(legacyUrl, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      // Always attempt to parse JSON, even on non-200
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.ok && (json.url || json.onboardingUrl)) {
        const url = json.url || json.onboardingUrl;
        // Store studioId in localStorage before redirect
        try {
          localStorage.setItem('mudo:stripe_onboarding_studioId', effectiveStudioId);
        } catch {}
        window.location.href = url;
        return { ok: true, connected: false, redirected: true };
      }
      // Log structured error details for diagnostics
      if (!res.ok) {
        console.error("[stripe] connect-onboard failed:", res.status, json);
      }
      // If status indicates platform restriction, surface clear toast and avoid redirect loops
      if (json?.status === "platform_restricted") {
        try {
          window.dispatchEvent?.(
            new CustomEvent("mudo:toast", {
              detail: {
                type: "error",
                message:
                  "Stripe Connect is restricted on your platform account — please check Stripe Dashboard → Settings → Connect.",
              },
            })
          );
        } catch {}
        return { ok: false, connected: false, error: "platform_restricted", status: res.status, data: json };
      }
      // If status indicates platform restriction, surface clear toast and avoid redirect loops
      if (json?.status === "platform_restricted") {
        try {
          window.dispatchEvent?.(
            new CustomEvent("mudo:toast", {
              detail: {
                type: "error",
                message:
                  "Stripe Connect is restricted on your platform account — please check Stripe Dashboard → Settings → Connect.",
              },
            })
          );
        } catch {}
        return { ok: false, connected: false, error: "platform_restricted" };
      }
      // Map friendly messages
      const code = json?.error || "unknown";
      const detail = json?.detail || "";
      let message = "Could not start Stripe onboarding";
      if (code === "connect_not_enabled") {
        message = json?.message || "Connect isn’t enabled on this Stripe account (test/live mismatch or Connect not activated).";
        // Surface a more visible banner/toast for capability issues
        try {
          window.dispatchEvent?.(
            new CustomEvent("mudo:toast", {
              detail: {
                type: "error",
                message,
              },
            })
          );
        } catch {}
      } else if (code === "stripe_key_mode_mismatch") {
        if (json?.overrideEnabled) {
          message = "Stripe keys are mixed (pk_test with sk_live). Override can’t fix this — make both live or both test.";
        } else {
          message = json?.message || "Stripe key mismatch (test vs live). Check Vercel env STRIPE_SECRET_KEY.";
        }
        try {
          window.dispatchEvent?.(
            new CustomEvent("mudo:toast", {
              detail: {
                type: "error",
                message,
              },
            })
          );
        } catch {}
      } else if (code === "studioId is required") {
        message = "Select a studio before connecting Stripe.";
      } else if (detail) {
        message = detail;
      }
      // Return a structured error for upstream UI to handle
      return { ok: false, connected: false, error: code, message, status: res.status, data: json };
    } catch (err) {
      try {
        window.dispatchEvent?.(
          new CustomEvent("mudo:toast", {
            detail: { type: "error", message: err?.message || String(err) },
          })
        );
      } catch {}
      return { ok: false, connected: false, error: err?.message || String(err), status: undefined, data: null };
    }
  },
    [activeStudioId, session?.access_token, stripeAccountId, studio?.id, studio?.studioId]
  );

  return { stripeAccountId, ensureStripeConnected, isStripeConnected: !!stripeAccountId };
}

export default useStripePayoutsGuard;
